import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import DocxUploader from "@/components/DocxUploader";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import MostCriticalDependencyBanner from "@/components/MostCriticalDependencyBanner";
import { useCollisions } from "@/hooks/useCollisions";
import { CollisionBadge, CollisionPanel } from "@/components/CollisionBadge";
import EcosystemDeploymentsHistory from "@/components/EcosystemDeploymentsHistory";
import { useTranslation } from "react-i18next";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Building2, User, Landmark, Coins, Users2, Heart, ShieldCheck,
  Globe2, Network, FileText, Eye, EyeOff, AlertTriangle, Briefcase, Handshake,
  Compass, Crown, Loader2, Layers, ArrowUpRight,
} from "lucide-react";

/* =========================================================================
 * Mapping kind → (icon, color)
 * ========================================================================= */
const KIND_META = {
  root:                  { icon: Crown,         color: "#EF9F27", group: "root" },
  annuaire_holding:      { icon: Building2,     color: "#378ADD", group: "annuaire" },
  annuaire_societe:      { icon: Briefcase,     color: "#378ADD", group: "annuaire" },
  annuaire_personne:     { icon: User,          color: "#9CA3AF", group: "annuaire" },
  annuaire_institution:  { icon: Landmark,      color: "#A78BFA", group: "annuaire" },
  annuaire_fonds:        { icon: Coins,         color: "#F472B6", group: "annuaire" },
  annuaire_famille:      { icon: Users2,        color: "#FB923C", group: "annuaire" },
  annuaire_etat:         { icon: ShieldCheck,   color: "#60A5FA", group: "annuaire" },
  annuaire_ong:          { icon: Heart,         color: "#F87171", group: "annuaire" },
  annuaire_org:          { icon: Globe2,        color: "#378ADD", group: "annuaire" },
  negotiation:           { icon: Handshake,     color: "#EF9F27", group: "negotiation" },
  deal:                  { icon: Compass,       color: "#1D9E75", group: "pipeline" },
  mandate:               { icon: FileText,      color: "#22C55E", group: "courtage" },
  chain:                 { icon: Network,       color: "#7F77DD", group: "chain" },
  risk:                  { icon: AlertTriangle, color: "#E24B4A", group: "risk" },
  trend_source:          { icon: Eye,           color: "#A1A1AA", group: "trend" },
  doctrine:              { icon: Crown,         color: "#FACC15", group: "doctrine" },
  // V22.8 — Cartographie de valeur, dépendances invisibles, asymétries
  asymmetry:             { icon: ArrowUpRight,  color: "#22D3EE", group: "asymmetry" },
  phantom:               { icon: EyeOff,        color: "#F472B6", group: "phantom" },
  utility_map:           { icon: Layers,        color: "#A78BFA", group: "utility" },
  bras_arme:             { icon: Crown,         color: "#EFD127", group: "bras_arme" },
};
const DEFAULT_META = { icon: Globe2, color: "#666", group: "other" };

const ALL_GROUPS = ["root", "annuaire", "negotiation", "pipeline", "courtage", "chain", "risk", "asymmetry", "phantom", "utility", "trend", "doctrine", "bras_arme"];

// V22.8 — Labels FR par défaut pour les groupes ontologie (fallback i18n).
// EN/DE sont gérés via les clés `saas.ontology.group_<name>` dans les locales.
const GROUP_DEFAULT_FR = {
  root: "Organisation",
  annuaire: "Annuaire",
  negotiation: "Négociations",
  pipeline: "Origination",
  courtage: "Courtage",
  chain: "Chaînages",
  risk: "Risques",
  asymmetry: "Asymétries",
  phantom: "Fantômes",
  utility: "Cartographies",
  trend: "Veille",
  doctrine: "Doctrine",
  bras_arme: "Super Partenaires",
};

/* =========================================================================
 * Custom node renderer
 * ========================================================================= */
function OntologyNode({ data }) {
  const meta = KIND_META[data.kind] || DEFAULT_META;
  const Icon = meta.icon;
  const isRoot = data.kind === "root";
  return (
    <div
      className={`rounded-md bg-[#0E0E10] border px-2.5 py-1.5 shadow-md transition-colors hover:border-white/30 relative`}
      style={{
        width: isRoot ? 220 : 180,
        borderColor: `${meta.color}55`,
      }}
      data-testid={`ontology-node-${data.kind}`}
      onClick={data.onCollisionClick}
    >
      <Handle type="target" position={Position.Left} style={{ background: meta.color, width: 5, height: 5, border: "none" }} />
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}1F`, border: `1px solid ${meta.color}55` }}
        >
          <Icon className="w-3 h-3" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[12px] font-medium text-zinc-100 truncate leading-tight ${isRoot ? "font-bold" : ""}`}>
            {data.label}
          </div>
          {data.sub && (
            <div className="text-[9px] text-zinc-500 truncate">{data.sub}</div>
          )}
        </div>
      </div>
      {data.collisions && data.collisions.length > 0 && (
        <div className="absolute -top-2 -right-2">
          <CollisionBadge collisions={data.collisions} />
        </div>
      )}
      <Handle type="source" position={Position.Right} style={{ background: meta.color, width: 5, height: 5, border: "none" }} />
    </div>
  );
}

const nodeTypes = { ontology: OntologyNode };

/* =========================================================================
 * Layout — anneaux concentriques par groupe sémantique
 * Racine au centre. Chaque groupe = anneau autour. Distribution angulaire.
 * ========================================================================= */
const RING_RADII = {
  annuaire: 1100,    // outermost (sera dense — 300+ entités)
  negotiation: 380,
  pipeline: 480,
  courtage: 560,
  chain: 640,
  risk: 760,
  trend: 880,
  doctrine: 260,
};

function layoutConcentric(nodes) {
  const positioned = [];
  // Grouper par catégorie sémantique
  const byGroup = {};
  for (const n of nodes) {
    const meta = KIND_META[n.kind] || DEFAULT_META;
    const grp = meta.group;
    (byGroup[grp] ||= []).push(n);
  }
  // Racine au centre
  for (const root of byGroup.root || []) {
    positioned.push({ ...root, position: { x: 0, y: 0 } });
  }
  // Anneaux concentriques
  for (const grp of ALL_GROUPS) {
    if (grp === "root") continue;
    const arr = byGroup[grp] || [];
    if (arr.length === 0) continue;
    const r = RING_RADII[grp] || 600;
    arr.forEach((n, i) => {
      const angle = (i / arr.length) * 2 * Math.PI - Math.PI / 2;
      positioned.push({
        ...n,
        position: { x: Math.cos(angle) * r, y: Math.sin(angle) * r },
      });
    });
  }
  return positioned;
}

/* =========================================================================
 * Main component
 * ========================================================================= */
export default function Ontologie() {
  const { t } = useTranslation();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGroups, setActiveGroups] = useState(new Set(ALL_GROUPS));
  const [view, setView] = useState("core"); // V23.14.4 — core | all | clusters
  // V23.15.42 — Souffle thermodynamique : agite légèrement les nœuds pour montrer
  // que l'ontologie n'est pas figée mais vivante. Toggle via bouton dédié.
  //
  // V23.15.56 — Mouvement organique. On remplace le sin/cos synchrone qui
  // dessinait des cercles ("atomes en rotation") par une superposition de
  // plusieurs sinus à fréquences irrationnelles (nombre d'or, √2, π/e) :
  // les orbites ne se ferment jamais → mouvement fluide, non répétitif,
  // qui évoque un système hors équilibre (thermodynamique du vivant).
  const [breathing, setBreathing] = useState(false);
  const [breathTime, setBreathTime] = useState(0);

  useEffect(() => {
    if (!breathing) return undefined;
    // V23.15.56 — Tick continu à ~30 FPS via setInterval (33ms). Un rAF pur
    // (60 FPS) déclenche l'overlay ResizeObserver de React Flow. 30 FPS reste
    // parfaitement fluide à l'œil et laisse React Flow respirer.
    const t0 = performance.now();
    const id = setInterval(() => {
      setBreathTime((performance.now() - t0) / 1000);
    }, 33);
    return () => clearInterval(id);
  }, [breathing]);

  useEffect(() => {
    setLoading(true);
    api.get(`/workspace/ontology/graph?view=${view}&max_nodes=30`)
      .then((r) => setGraph(r.data))
      .catch(() => setGraph({ nodes: [], edges: [], stats: { total_nodes: 0, total_edges: 0, cross_module_edges: 0, total_full_graph_nodes: 0, by_kind: {} } }))
      .finally(() => setLoading(false));
  }, [view]);

  /* Apply group filter */
  const filteredNodes = useMemo(() => {
    if (!graph) return [];
    return graph.nodes.filter((n) => {
      const grp = (KIND_META[n.kind] || DEFAULT_META).group;
      return activeGroups.has(grp);
    });
  }, [graph, activeGroups]);

  /* V23.15.58 — Mécanique de Collision : hook posé AVANT rfNodes pour que
     collisionsByEntity soit accessible dans le useMemo qui construit les nœuds.
     Le hook lit les positions via un ref qu'on met à jour dans le useMemo. */
  const nodesForCollisionRef = useRef([]);
  const {
    collisionsByEntity,
    analysisCount: collisionAnalysisCount,
    quotaExceeded: collisionQuotaExceeded,
  } = useCollisions({
    active: breathing,
    surface: "ontologie",
    getEntities: () => nodesForCollisionRef.current,
    getPosition: (e) => e._pos,
  });
  const [openCollision, setOpenCollision] = useState(null);

  const rfNodes = useMemo(() => {
    const positioned = layoutConcentric(filteredNodes);
    const built = positioned.map((n, idx) => {
      let jitter = { x: 0, y: 0 };
      if (breathing) {
        const t = breathTime;
        const phaseX = idx * 1.7;
        const phaseY = idx * 2.3;
        const amp = 14 + 4 * Math.sin(t * 0.35 + idx);
        const jx =
          Math.sin(t * 1.618 + phaseX) * amp +
          Math.sin(t * 1.414 + phaseX * 1.7) * amp * 0.45 +
          Math.sin(t * 1.156 + phaseX * 2.9) * amp * 0.25;
        const jy =
          Math.sin(t * 1.732 + phaseY) * amp +
          Math.sin(t * 1.272 + phaseY * 1.3) * amp * 0.45 +
          Math.sin(t * 1.087 + phaseY * 2.1) * amp * 0.25;
        jitter = { x: jx, y: jy };
      }
      const pos = { x: n.position.x + jitter.x, y: n.position.y + jitter.y };
      return {
        id: n.id,
        type: "ontology",
        position: pos,
        data: {
          label: n.label,
          kind: n.kind,
          sub: n.sub || "",
          collisions: (collisionsByEntity && collisionsByEntity[n.id]) || [],
          onCollisionClick: (collisionsByEntity && collisionsByEntity[n.id] && collisionsByEntity[n.id].length > 0)
            ? () => setOpenCollision([...collisionsByEntity[n.id]].sort((a, b) => (b.score || 0) - (a.score || 0))[0])
            : undefined,
        },
        draggable: true,
        _pos: pos,
        _label: n.label,
        _kind: n.kind,
        _sub: n.sub,
      };
    });
    // Met à jour le ref utilisé par useCollisions
    nodesForCollisionRef.current = built.map((rn) => ({
      id: rn.id,
      name: rn._label || rn.id,
      kind: rn._kind,
      role: rn._sub,
      _pos: rn._pos,
    }));
    return built;
  }, [filteredNodes, breathing, breathTime, collisionsByEntity]);

  const visibleIds = useMemo(() => new Set(rfNodes.map((n) => n.id)), [rfNodes]);

  const rfEdges = useMemo(() => {
    if (!graph) return [];
    return graph.edges
      .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
      .map((e, i) => ({
        id: `e-${i}-${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        type: "default",
        style: { stroke: "rgba(255,255,255,0.07)", strokeWidth: 1 },
        markerEnd: { type: MarkerType.ArrowClosed, color: "rgba(255,255,255,0.2)", width: 8, height: 8 },
      }));
  }, [graph, visibleIds]);

  const toggleGroup = (grp) => {
    setActiveGroups((prev) => {
      const next = new Set(prev);
      if (next.has(grp)) next.delete(grp);
      else next.add(grp);
      return next;
    });
  };

  const stats = graph?.stats || { total_nodes: 0, total_edges: 0, cross_module_edges: 0, by_kind: {} };

  return (
    <div className="space-y-5" data-testid="ontologie-root">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="noam-overline">{t("saas.ontology.overline", { defaultValue: "Cartographie cross-modules" })}</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">{t("saas.ontology.title", { defaultValue: "Ontologie" })}</h1>
          <p className="text-zinc-400 text-sm mt-1">
            {t("saas.ontology.subtitle_v2", { defaultValue: "Graphe sémantique vivant de l'environnement Noam — entités, négociations, deals, mandats, chaînages, risques, veille et doctrine." })}
          </p>
        </div>
        <ModuleHelpButton moduleKey="ontologie" />
      </div>

      {/* V23.15.32 — Doctrine 3Q · "Et alors ?" en tête de vue */}
      <MostCriticalDependencyBanner />

      {/* V23.15.42 — Souffle thermodynamique : montre que le graphe est vivant
           V23.15.58 — Renommé « Collision » : le mouvement devient un accélérateur
           de sérendipité doctrinale (voir hook useCollisions). */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setBreathing((v) => !v)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all inline-flex items-center gap-1.5 ${
            breathing
              ? "border-[#7F77DD] bg-[#7F77DD]/15 text-[#7F77DD] shadow-[0_0_16px_rgba(127,119,221,0.35)]"
              : "border-white/10 text-zinc-400 hover:border-[#7F77DD]/50 hover:text-[#7F77DD]"
          }`}
          data-testid="ontology-breath-btn"
          title="Active le mode Collision — les acteurs entrent en mouvement organique et Noam détecte les adjacences porteuses d'opportunités ou de risques systémiques."
        >
          <span className={`w-2 h-2 rounded-full ${breathing ? "bg-[#7F77DD] animate-pulse" : "bg-zinc-600"}`} />
          {breathing ? "Mode Collision actif" : "Activer le mode Collision"}
        </button>
        {breathing && collisionQuotaExceeded && (
          <span className="text-[10px] text-[#EF9F27]">
            5 collisions analysées — pour aller au-delà, passez en offre premium.
          </span>
        )}
        {breathing && !collisionQuotaExceeded && (
          <span className="text-[10px] text-zinc-500 italic">
            {collisionAnalysisCount}/5 collisions analysées cette session — laisse Noam trouver ce que personne d&apos;autre ne voit.
          </span>
        )}
      </div>

      {/* V23.14.4 — Vue toggle (Pareto) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="ontology-view-toggle">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">{t("saas.ontology.view_label", { defaultValue: "Vue" })}</span>
        {["core", "clusters", "all"].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`text-xs px-3 py-1.5 rounded border transition ${
              view === v
                ? "border-[#EFD127] bg-[#EFD127]/10 text-[#EFD127]"
                : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
            data-testid={`ontology-view-${v}-btn`}
          >
            {v === "core"
              ? t("saas.ontology.view_core", { defaultValue: "Essentiel (Pareto)" })
              : v === "clusters"
              ? t("saas.ontology.view_clusters", { defaultValue: "Clusters" })
              : t("saas.ontology.view_all", { defaultValue: `Tout le réseau (${stats.total_full_graph_nodes || stats.total_nodes})` })}
          </button>
        ))}
        <span className="text-[11px] text-zinc-500 ml-1">
          {t("saas.ontology.showing", {
            visible: stats.total_nodes,
            total: stats.total_full_graph_nodes || stats.total_nodes,
            defaultValue: `Affiché : ${stats.total_nodes} / ${stats.total_full_graph_nodes || stats.total_nodes}`,
          })}
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid md:grid-cols-4 gap-3" data-testid="ontology-kpis">
        <Kpi label={t("saas.ontology.k_nodes", { defaultValue: "Nœuds" })} value={stats.total_nodes} />
        <Kpi label={t("saas.ontology.k_edges", { defaultValue: "Relations" })} value={stats.total_edges} />
        <Kpi label={t("saas.ontology.k_cross", { defaultValue: "Liens cross-modules" })} value={stats.cross_module_edges} accent="#EF9F27" />
        <Kpi label={t("saas.ontology.k_kinds", { defaultValue: "Types représentés" })} value={Object.keys(stats.by_kind).length} />
      </div>

      {/* Group filters */}
      <div className="flex flex-wrap gap-1.5" data-testid="ontology-filters">
        {ALL_GROUPS.map((grp) => {
          const active = activeGroups.has(grp);
          const sampleKind = Object.keys(KIND_META).find((k) => KIND_META[k].group === grp);
          const color = (KIND_META[sampleKind] || DEFAULT_META).color;
          return (
            <button
              key={grp}
              onClick={() => toggleGroup(grp)}
              className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors`}
              style={{
                background: active ? `${color}22` : "transparent",
                borderColor: active ? `${color}66` : "rgba(255,255,255,0.1)",
                color: active ? color : "#71717A",
              }}
              data-testid={`ontology-filter-${grp}`}
            >
              {t(`saas.ontology.group_${grp}`, { defaultValue: GROUP_DEFAULT_FR[grp] || grp })}
            </button>
          );
        })}
      </div>

      {/* Graph */}
      <div className="noam-card p-0 overflow-hidden" style={{ height: 680 }} data-testid="ontology-graph">
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
          </div>
        ) : rfNodes.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            <Network className="w-10 h-10 mx-auto text-[#378ADD] opacity-40 mb-3" />
            {t("saas.ontology.empty", { defaultValue: "Aucune donnée à cartographier dans ce workspace." })}
          </div>
        ) : (
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            colorMode="dark"
            proOptions={{ hideAttribution: true }}
            nodesDraggable
            nodesConnectable={false}
            elementsSelectable
            minZoom={0.05}
            maxZoom={2}
          >
            <Background gap={24} color="#1f2937" />
            <Controls showInteractive={false} />
            <MiniMap
              pannable
              zoomable
              nodeColor={(n) => (KIND_META[n.data?.kind] || DEFAULT_META).color}
              maskColor="rgba(0,0,0,0.6)"
              style={{ background: "#0E0E10", border: "1px solid rgba(255,255,255,0.1)" }}
            />
          </ReactFlow>
        )}
      </div>

      {/* By-kind distribution */}
      {Object.keys(stats.by_kind).length > 0 && (
        <div className="noam-card p-4" data-testid="ontology-distribution">
          <div className="noam-overline mb-3">{t("saas.ontology.distribution", { defaultValue: "Distribution par type" })}</div>
          <div className="grid md:grid-cols-4 lg:grid-cols-6 gap-2 text-xs">
            {Object.entries(stats.by_kind)
              .sort(([, a], [, b]) => b - a)
              .map(([kind, n]) => {
                const meta = KIND_META[kind] || DEFAULT_META;
                return (
                  <div key={kind} className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/5 bg-black/20">
                    <div className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                    <span className="text-zinc-300 truncate">{kind.replace(/_/g, " ")}</span>
                    <span className="text-zinc-500 ml-auto font-mono">{n}</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* P2 2026-09 — Historique des grappes déployées depuis le chat */}
      <EcosystemDeploymentsHistory />

      <div className="noam-card p-5">
        <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="noam-overline">{t("saas.ontology.scope_notes", { defaultValue: "Documents d'ontologie" })}</div>
            <div className="text-sm text-zinc-300 mt-1">
              {t("saas.ontology.scope_help", { defaultValue: "Importez des documents pour enrichir le contexte de Noam." })}
            </div>
          </div>
        </div>
        <DocxUploader module="ontologie" title="" />
      </div>

      {/* V23.15.58 — Panneau de collision doctrinale (opportunité/risque systémique) */}
      <CollisionPanel collision={openCollision} onClose={() => setOpenCollision(null)} />
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="noam-card p-4">
      <div className="noam-overline mb-1">{label}</div>
      <div className="noam-display text-3xl font-bold" style={{ color: accent || "#fff" }}>
        {value}
      </div>
    </div>
  );
}
